import dicomParser from 'dicom-parser';

// Some source systems export DICOM files whose Pixel Data is genuinely
// compressed (JPEG-LS / JPEG Lossless / JPEG 2000), structured as a proper
// encapsulated sequence (Basic Offset Table + fragment Items), but the file
// metadata is wrong in two ways: the Transfer Syntax UID (0002,0010) still
// says "uncompressed" (Explicit VR Little Endian), and the outer Pixel Data
// element length is a concrete byte count instead of the 0xFFFFFFFF
// "undefined length" marker the DICOM standard requires for encapsulated
// data. cornerstone-wado-image-loader trusts both fields at face value, so
// it tries to read the compressed bytes as if they were raw uncompressed
// pixels and the size math blows up (`RangeError: Invalid typed array
// length`). This rewrites just those two header fields — the compressed
// payload itself is left untouched — so the existing (already correct)
// encapsulated-pixel-data decoding path picks it up normally.
const ITEM_TAG_BYTES = [0xfe, 0xff, 0x00, 0xe0];

const TRANSFER_SYNTAXES = {
  jpegBaseline: { lossless: '1.2.840.10008.1.2.4.50', lossy: '1.2.840.10008.1.2.4.50' },
  jpegExtended: { lossless: '1.2.840.10008.1.2.4.51', lossy: '1.2.840.10008.1.2.4.51' },
  jpegLossless: { lossless: '1.2.840.10008.1.2.4.70', lossy: '1.2.840.10008.1.2.4.70' },
  jpegLS: { lossless: '1.2.840.10008.1.2.4.80', lossy: '1.2.840.10008.1.2.4.81' },
  jpeg2000: { lossless: '1.2.840.10008.1.2.4.90', lossy: '1.2.840.10008.1.2.4.91' },
};

function hasItemTagAt(byteArray, offset) {
  if (offset + 4 > byteArray.length) {
    return false;
  }
  return ITEM_TAG_BYTES.every((b, i) => byteArray[offset + i] === b);
}

function readUint32LE(byteArray, offset) {
  return (
    byteArray[offset] |
    (byteArray[offset + 1] << 8) |
    (byteArray[offset + 2] << 16) |
    (byteArray[offset + 3] << 24)
  ) >>> 0;
}

// Locates the start of the first fragment's compressed bytes, skipping the
// Basic Offset Table item and however many offset-table entries it declares.
function findFirstFragmentDataOffset(byteArray, pixelDataOffset) {
  if (!hasItemTagAt(byteArray, pixelDataOffset)) {
    return null;
  }
  const botLength = readUint32LE(byteArray, pixelDataOffset + 4);
  const fragmentItemOffset = pixelDataOffset + 8 + botLength;

  if (!hasItemTagAt(byteArray, fragmentItemOffset)) {
    return null;
  }

  return fragmentItemOffset + 8;
}

function detectCodec(byteArray, frameOffset) {
  if (frameOffset + 4 > byteArray.length) {
    return null;
  }

  if (byteArray[frameOffset] === 0xff && byteArray[frameOffset + 1] === 0xd8) {
    // JPEG family (SOI marker) - the next marker identifies the flavor.
    const marker = byteArray[frameOffset + 3];
    if (marker === 0xf7) {
      return 'jpegLS'; // SOF55
    }
    if (marker === 0xc3) {
      return 'jpegLossless'; // SOF3
    }
    if (marker === 0xc1) {
      return 'jpegExtended'; // SOF1
    }
    if (marker === 0xc0) {
      return 'jpegBaseline'; // SOF0
    }
    return null;
  }

  if (byteArray[frameOffset] === 0xff && byteArray[frameOffset + 1] === 0x4f) {
    return 'jpeg2000'; // raw JP2K codestream (SOC marker)
  }

  return null;
}

function buildCorrectedTransferSyntaxUID(codec, isLossy) {
  const uids = TRANSFER_SYNTAXES[codec];
  return isLossy ? uids.lossy : uids.lossless;
}

async function readFileArrayBuffer(file) {
  if (typeof file.arrayBuffer === 'function') {
    return file.arrayBuffer();
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

// Core fix, operating on raw bytes - shared by the local-file loader (a
// browser File) and the DICOMweb loader (an XHR response ArrayBuffer).
// Returns the original byteArray unchanged unless the mislabeling signature
// is found.
export function fixMislabeledTransferSyntaxBytes(byteArray) {
  let dataSet;
  try {
    dataSet = dicomParser.parseDicom(byteArray);
  } catch (e) {
    // Not a parseable DICOM file (e.g. a PDF) - nothing for us to do.
    return byteArray;
  }

  const pixelDataElement = dataSet.elements.x7fe00010;
  if (!pixelDataElement || pixelDataElement.encapsulatedPixelData) {
    return byteArray;
  }

  const rows = dataSet.uint16('x00280010');
  const columns = dataSet.uint16('x00280011');
  const bitsAllocated = dataSet.uint16('x00280100');
  const samplesPerPixel = dataSet.uint16('x00280002') || 1;
  const numberOfFrames = dataSet.intString('x00280008') || 1;

  if (!rows || !columns || !bitsAllocated) {
    return byteArray;
  }

  const bytesPerSample = Math.ceil(bitsAllocated / 8);
  const expectedUncompressedLength =
    rows * columns * samplesPerPixel * bytesPerSample * numberOfFrames;

  if (pixelDataElement.length >= expectedUncompressedLength) {
    // Genuinely uncompressed (or something else entirely) - leave it alone.
    return byteArray;
  }

  const frameOffset = findFirstFragmentDataOffset(
    byteArray,
    pixelDataElement.dataOffset
  );
  if (frameOffset === null) {
    return byteArray;
  }

  const codec = detectCodec(byteArray, frameOffset);
  if (!codec) {
    return byteArray;
  }

  const isLossy = dataSet.string('x00282110') === '01';
  const correctedTransferSyntaxUID = buildCorrectedTransferSyntaxUID(
    codec,
    isLossy
  );

  const tsElement = dataSet.elements.x00020010;
  const groupLengthElement = dataSet.elements.x00020000;
  if (!tsElement || !groupLengthElement) {
    return byteArray;
  }

  let newTsUidBytes = correctedTransferSyntaxUID
    .split('')
    .map(c => c.charCodeAt(0));
  if (newTsUidBytes.length % 2 !== 0) {
    newTsUidBytes.push(0);
  }

  const delta = newTsUidBytes.length - tsElement.length;

  const correctedBytes = new Uint8Array(byteArray.length + delta);
  correctedBytes.set(byteArray.subarray(0, tsElement.dataOffset), 0);
  correctedBytes.set(newTsUidBytes, tsElement.dataOffset);
  correctedBytes.set(
    byteArray.subarray(tsElement.dataOffset + tsElement.length),
    tsElement.dataOffset + newTsUidBytes.length
  );

  // Fix up the Transfer Syntax UID element's own 2-byte length field (UI is
  // a short-form explicit VR: tag[4] + VR[2] + length[2], so the field sits
  // right before dataOffset) - it still says the old byte length otherwise,
  // which desyncs every element read after it.
  const tsLengthFieldOffset = tsElement.dataOffset - 2;
  correctedBytes[tsLengthFieldOffset] = newTsUidBytes.length & 0xff;
  correctedBytes[tsLengthFieldOffset + 1] =
    (newTsUidBytes.length >>> 8) & 0xff;

  // Fix up the File Meta Group Length (0002,0000) value to account for the
  // Transfer Syntax UID's new byte length.
  const oldGroupLength = dataSet.uint32('x00020000');
  const newGroupLength = oldGroupLength + delta;
  const groupLengthValueOffset = groupLengthElement.dataOffset;
  correctedBytes[groupLengthValueOffset] = newGroupLength & 0xff;
  correctedBytes[groupLengthValueOffset + 1] = (newGroupLength >>> 8) & 0xff;
  correctedBytes[groupLengthValueOffset + 2] = (newGroupLength >>> 16) & 0xff;
  correctedBytes[groupLengthValueOffset + 3] = (newGroupLength >>> 24) & 0xff;

  // Mark the Pixel Data element as having undefined length (0xFFFFFFFF),
  // the standard's signal for "this is an encapsulated sequence of
  // fragments" - the 4-byte length field sits right before dataOffset for
  // explicit VR OB (tag[4] + VR[2] + reserved[2] + length[4]).
  const newPixelDataLengthFieldOffset = pixelDataElement.dataOffset - 4 + delta;
  correctedBytes[newPixelDataLengthFieldOffset] = 0xff;
  correctedBytes[newPixelDataLengthFieldOffset + 1] = 0xff;
  correctedBytes[newPixelDataLengthFieldOffset + 2] = 0xff;
  correctedBytes[newPixelDataLengthFieldOffset + 3] = 0xff;

  return correctedBytes;
}

// File-based convenience wrapper for the local (/local) file loading path.
export default async function fixMislabeledTransferSyntax(file) {
  let arrayBuffer;
  try {
    arrayBuffer = await readFileArrayBuffer(file);
  } catch (e) {
    return file;
  }

  const byteArray = new Uint8Array(arrayBuffer);
  const correctedBytes = fixMislabeledTransferSyntaxBytes(byteArray);

  if (correctedBytes === byteArray) {
    return file;
  }

  return new File([correctedBytes], file.name, {
    type: file.type,
    lastModified: file.lastModified,
  });
}
